import {
  Alert,
  Button,
  Flex,
  Grid,
  Group,
  Modal,
  RemoveScroll,
  ScrollArea,
  Select,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import LoaderOverlay from '../../components/LoaderOverlay/LoaderOverlay';
import { useForm } from '@mantine/form';
import { requiredFieldFormValidator } from '../../utils/validators/formValidators';
import { getBackendErrorMessage } from '../../utils/Notification/BackendErrorsHandler';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { DateInput } from '@mantine/dates';
import { IProduct } from '../../interfaces/Product';
import UsoCFDIEnum, { UsoCFDIType } from '../../enum/UsoCFDIEnum';
import MetodoPagoCFDIEnum, { MetodoPagoCFDType } from '../../enum/MetodoPagoCFDIEnum';
import FormaPagoCFDIEnum, { FormaPagoCFDIType } from '../../enum/FormaPagoCFDIEnum';
import SATPeriodicityEnum, { SATPeriodicityType } from '../../enum/SATPeriodicityEnum';
import SATMonthsEnum, { SATMonthsType } from '../../enum/SATMonthsEnum';
import { IconInfoCircle } from '@tabler/icons-react';
import { formatMoneyString } from '../../utils/strings';
import { useHistory } from 'react-router-dom';
import CFDIPreviewPartySummary from '../CFDI/CFDIPreviewPartySummary';
import RegimenFiscalEnum, { RegimenFiscalType } from '../../enum/RegimenFiscalEnum';
import {
  areCFDICustomerFiscalProfilesEqual,
  getCFDICustomerFiscalProfileFromPreview,
  getMissingCFDICustomerFiscalProfileFields,
  normalizeCFDICustomerFiscalProfile,
  validateCFDICustomerFiscalProfile
} from '@washday/utilities';

type CFDIProduct = IProduct & {
  product_key?: string;
  unit_key?: string;
};

export default memo(function OrderCreateCFDIModal({
  opened,
  close,
  title,
  isCFDIGlobal = false,
  onCreateCFDI,
  isLoading = false,
  CFDIInfo,
  previewError,
  enableCustomerFiscalDataEditing = false,
  canEditCustomerFiscalData = false
}: {
  opened: boolean;
  close: () => void;
  title: string;
  onCreateCFDI: (values: {
    date: Date;
    use: string;
    payment_method: string;
    payment_form: string;
    sendEmailInvoice: boolean;
    orderID: string;
    facturapiInvoiceID: string;
    global: {
      periodicity: string;
      months: string;
      year: string;
    };
    customerFiscalProfile?: import('@washday/utilities').CFDICustomerFiscalProfile;
  }) => void;
  isCFDIGlobal?: boolean;
  isLoading: boolean;
  previewError?: any;
  enableCustomerFiscalDataEditing?: boolean;
  canEditCustomerFiscalData?: boolean;
  CFDIInfo?: {
    emisor: {
      name: string;
      tax_system: string;
    };
    customer: {
      legal_name: string;
      tax_id: string;
      email: string;
      tax_system: string;
      address?: {
        zip?: string;
      };
    };
    date: Date;
    use?: string;
    payment_method?: string;
    payment_form?: string;
    sendEmailInvoice?: boolean;
    items?: { discount: number; product: IProduct; quantity: number }[];
    orderID?: string;
    facturapiInvoiceID?: string;
    global: {
      periodicity?: string;
      months?: string;
      year?: string;
    };
  };
}) {
  const { t } = useTranslation(['OrdersPage', 'FieldName', 'CommonButtons', 'CFDIPage']);
  const history = useHistory();
  const [showPUEPaymentFormAlert, setShowPUEPaymentFormAlert] = useState(false);
  const [disablePaymentFormField, setDisablePaymentFormField] = useState(false);
  const [isEditingCustomerFiscalData, setIsEditingCustomerFiscalData] = useState(false);
  const initializedCustomerFiscalSessionRef = useRef<string | null>(null);
  const originalCustomerFiscalProfile = useMemo(
    () => getCFDICustomerFiscalProfileFromPreview(CFDIInfo?.customer),
    [CFDIInfo?.customer]
  );
  const hasMissingCustomerFiscalData = useMemo(
    () =>
      getMissingCFDICustomerFiscalProfileFields(originalCustomerFiscalProfile).length > 0,
    [originalCustomerFiscalProfile]
  );
  const useCFDIOptions = useMemo(() => {
    return Object.keys(UsoCFDIEnum).map((key: string) => {
      return {
        value: key,
        key: key,
        label: `${key} - ${UsoCFDIEnum[key as UsoCFDIType]}`
      };
    });
  }, []);

  let rows = [];
  const form = useForm({
    mode: 'uncontrolled',
    initialValues: {
      date: new Date(),
      use: '',
      payment_method: '',
      payment_form: '',
      sendEmailInvoice: false,
      orderID: '',
      facturapiInvoiceID: '',
      customerFiscalProfile: {
        rfc: '',
        legalName: '',
        taxSystem: '',
        zipCode: ''
      },
      global: {
        periodicity: '',
        months: '',
        year: ''
      }
    },
    validate: {
      date: (value) => {
        const requiredValidatorRes = requiredFieldFormValidator(value);
        if (requiredValidatorRes) return requiredValidatorRes;
      },
      use: (value) => {
        const requiredValidatorRes = requiredFieldFormValidator(value);
        if (requiredValidatorRes) return requiredValidatorRes;
      },
      payment_method: (value) => {
        const requiredValidatorRes = requiredFieldFormValidator(value);
        if (requiredValidatorRes) return requiredValidatorRes;
      },
      payment_form: (value) => {
        const requiredValidatorRes = requiredFieldFormValidator(value);
        if (requiredValidatorRes) return requiredValidatorRes;
      },
      customerFiscalProfile: {
        rfc: (value) => {
          if (
            !enableCustomerFiscalDataEditing ||
            isCFDIGlobal ||
            !canEditCustomerFiscalData ||
            !isEditingCustomerFiscalData
          ) return null;
          const code = validateCFDICustomerFiscalProfile({
            ...form.getValues().customerFiscalProfile,
            rfc: value
          }).rfc;
          return code ? t(`OrdersPage:createCFDI.customerFiscalDataErrors.${code}`) : null;
        },
        legalName: (value) => {
          if (
            !enableCustomerFiscalDataEditing ||
            isCFDIGlobal ||
            !canEditCustomerFiscalData ||
            !isEditingCustomerFiscalData
          ) return null;
          const code = validateCFDICustomerFiscalProfile({
            ...form.getValues().customerFiscalProfile,
            legalName: value
          }).legalName;
          return code ? t(`OrdersPage:createCFDI.customerFiscalDataErrors.${code}`) : null;
        },
        taxSystem: (value) => {
          if (
            !enableCustomerFiscalDataEditing ||
            isCFDIGlobal ||
            !canEditCustomerFiscalData ||
            !isEditingCustomerFiscalData
          ) return null;
          const code = validateCFDICustomerFiscalProfile({
            ...form.getValues().customerFiscalProfile,
            taxSystem: value
          }).taxSystem;
          return code ? t(`OrdersPage:createCFDI.customerFiscalDataErrors.${code}`) : null;
        },
        zipCode: (value) => {
          if (
            !enableCustomerFiscalDataEditing ||
            isCFDIGlobal ||
            !canEditCustomerFiscalData ||
            !isEditingCustomerFiscalData
          ) return null;
          const code = validateCFDICustomerFiscalProfile({
            ...form.getValues().customerFiscalProfile,
            zipCode: value
          }).zipCode;
          return code ? t(`OrdersPage:createCFDI.customerFiscalDataErrors.${code}`) : null;
        }
      },
      global: {
        periodicity: (value) => {
          if (isCFDIGlobal) {
            const requiredValidatorRes = requiredFieldFormValidator(value);
            if (requiredValidatorRes) return requiredValidatorRes;
          }
        },
        months: (value) => {
          if (isCFDIGlobal) {
            const requiredValidatorRes = requiredFieldFormValidator(value);
            if (requiredValidatorRes) return requiredValidatorRes;
          }
        },
        year: (value) => {
          if (isCFDIGlobal) {
            const requiredValidatorRes = requiredFieldFormValidator(value);
            if (requiredValidatorRes) return requiredValidatorRes;
          }
        }
      }
    }
  });

  const onMetodoPagoCFDIChange = (value: string | null) => {
    //ASIGNAR Y BLOQUEAR PAYMEN FORM COMO CODIGO 99;
    if (value === 'PPD') {
      setDisablePaymentFormField(true);
      setShowPUEPaymentFormAlert(true);
    } else {
      setDisablePaymentFormField(false);
      setShowPUEPaymentFormAlert(false);
    }
  };

  form.watch('payment_method', ({ value }) => {
    onMetodoPagoCFDIChange(value);
  });

  useEffect(() => {
    if (disablePaymentFormField) {
      form.setFieldValue('payment_form', '99');
    }
  }, [disablePaymentFormField]);

  type FormValues = typeof form.values;

  const handleSubmit = async (values: FormValues) => {
    try {
      const { customerFiscalProfile, ...invoiceValues } = values;
      const normalizedFiscalProfile = normalizeCFDICustomerFiscalProfile(
        customerFiscalProfile
      );
      const fiscalProfileChanged = !areCFDICustomerFiscalProfilesEqual(
        originalCustomerFiscalProfile,
        normalizedFiscalProfile
      );
      await onCreateCFDI({
        ...invoiceValues,
        ...(enableCustomerFiscalDataEditing &&
          !isCFDIGlobal &&
          canEditCustomerFiscalData &&
          isEditingCustomerFiscalData &&
          fiscalProfileChanged
          ? { customerFiscalProfile: normalizedFiscalProfile }
          : {})
      });
    } catch (e) {
      console.log(e);
    }
  };

  const years: {
    label: string;
    value: string;
  }[] = useMemo(() => {
    const last5Years = () => {
      const currentYear = new Date().getFullYear();
      const years = [];

      // Generate the last 5 years and add them to the 'years' array
      for (let i = 0; i < 5; i++) {
        const year = currentYear - i;
        years.push({
          label: year.toString(),
          value: year.toString()
        });
      }

      return years;
    };
    return last5Years();
  }, []);

  useEffect(() => {
    if (!opened) {
      form.reset();
      setIsEditingCustomerFiscalData(false);
      initializedCustomerFiscalSessionRef.current = null;
      return;
    }

    if (!CFDIInfo) return;

    const fiscalSessionKey = enableCustomerFiscalDataEditing
      ? CFDIInfo.orderID || 'orders-cfdi-session'
      : null;
    if (
      fiscalSessionKey &&
      initializedCustomerFiscalSessionRef.current === fiscalSessionKey
    ) {
      return;
    }

    const previewDate = CFDIInfo.date ? new Date(CFDIInfo.date) : null;
    if (!previewDate || Number.isNaN(previewDate.getTime())) {
      return;
    }

    const initialValues = {
      date: previewDate,
      use: CFDIInfo.use || '',
      payment_method: CFDIInfo.payment_method || '',
      payment_form: CFDIInfo.payment_form || '',
      sendEmailInvoice: CFDIInfo.sendEmailInvoice || false,
      orderID: CFDIInfo?.orderID || '',
      facturapiInvoiceID: CFDIInfo?.facturapiInvoiceID || '',
      customerFiscalProfile: originalCustomerFiscalProfile,
      global: {
        periodicity: CFDIInfo.global?.periodicity || '',
        months: CFDIInfo.global?.months || '',
        year: CFDIInfo.global?.year || ''
      }
    };
    form.setValues(initialValues);
    form.setInitialValues(initialValues);
    form.resetDirty(initialValues);
    initializedCustomerFiscalSessionRef.current = fiscalSessionKey;
    setIsEditingCustomerFiscalData(
      enableCustomerFiscalDataEditing &&
        !isCFDIGlobal &&
        canEditCustomerFiscalData &&
        hasMissingCustomerFiscalData
    );
    // Mantine form methods are stable; depending on the wrapper object would reset edits per render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    opened,
    CFDIInfo,
    isCFDIGlobal,
    enableCustomerFiscalDataEditing,
    canEditCustomerFiscalData,
    originalCustomerFiscalProfile,
    hasMissingCustomerFiscalData
  ]);

  const hasCFDIPreview = Boolean(
    CFDIInfo?.emisor && CFDIInfo?.customer && Array.isArray(CFDIInfo?.items)
  );
  const previewErrorMessage = useMemo(() => {
    return (
      getBackendErrorMessage(previewError) || t('OrdersPage:createCFDI.previewLoadError')
    );
  }, [previewError, t]);
  const shouldShowPreviewError = !isLoading && !hasCFDIPreview;
  const existingCFDIId = CFDIInfo?.facturapiInvoiceID;
  const existingCFDILink = existingCFDIId ? (
    <a
      href={`/CFDI?q=${existingCFDIId}`}
      onClick={(event) => {
        event.preventDefault();
        history.push(`/CFDI?q=${existingCFDIId}`);
      }}
    >
      {t('OrdersPage:createCFDI.viewCFDI')}
    </a>
  ) : null;

  rows = [...(CFDIInfo?.items || [])].map(
    (element: { discount: number; product: CFDIProduct; quantity: number }, idx: number) => (
      <Table.Tr key={element.product._id || idx}>
        <Table.Td>{element.product.product_key || '-'}</Table.Td>
        <Table.Td>{element.product.description}</Table.Td>
        <Table.Td>{element.product.unit_key || '-'}</Table.Td>
        <Table.Td>{element.product.sku}</Table.Td>
        <Table.Td>{formatMoneyString(element.product.price)}</Table.Td>
        <Table.Td>{element.quantity}</Table.Td>
        <Table.Td>{formatMoneyString(element.discount)}</Table.Td>
      </Table.Tr>
    )
  );

  return (
    <Modal
      opened={opened}
      onClose={close}
      className={RemoveScroll.classNames.fullWidth}
      title={title}
      scrollAreaComponent={ScrollArea.Autosize}
    >
      <LoaderOverlay visible={isLoading} />
      {!isLoading && (
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Grid gutter="md">
            {shouldShowPreviewError ? (
              <Grid.Col span={12}>
                <Alert color="red" icon={<IconInfoCircle />}>
                  {previewErrorMessage}
                  {existingCFDILink ? <div>{existingCFDILink}</div> : null}
                </Alert>
              </Grid.Col>
            ) : (
              <>
                <Grid.Col span={12}>
                  {CFDIInfo?.facturapiInvoiceID ? (
                    <Alert color="yellow" icon={<IconInfoCircle />}>
                      {t('OrdersPage:createCFDI.replacementWarning')}
                      {existingCFDILink}
                    </Alert>
                  ) : (
                    <></>
                  )}
                </Grid.Col>
                <CFDIPreviewPartySummary
                  emisor={CFDIInfo?.emisor}
                  customer={CFDIInfo?.customer}
                  hideCustomer={enableCustomerFiscalDataEditing && !isCFDIGlobal}
                />
                {enableCustomerFiscalDataEditing && !isCFDIGlobal && (
                  <Grid.Col span={{ xs: 12, md: 6 }}>
                    <Flex direction="column">
                    <Group justify="space-between" align="center" mt="md" mb={10}>
                      <Title order={4}>{t('CFDIPage:preview.receptor')}</Title>
                      {canEditCustomerFiscalData && !isEditingCustomerFiscalData && (
                        <Button
                          type="button"
                          variant="subtle"
                          size="compact-sm"
                          onClick={() => setIsEditingCustomerFiscalData(true)}
                        >
                          {t('OrdersPage:createCFDI.editCustomerFiscalData')}
                        </Button>
                      )}
                    </Group>
                    {isEditingCustomerFiscalData ? (
                      <>
                        <TextInput
                          key={form.key('customerFiscalProfile.rfc')}
                          {...form.getInputProps('customerFiscalProfile.rfc')}
                          label={t('FieldName:rfc')}
                        />
                        <TextInput
                          key={form.key('customerFiscalProfile.legalName')}
                          {...form.getInputProps('customerFiscalProfile.legalName')}
                          label={t('FieldName:legal_name')}
                        />
                        <Select
                          key={form.key('customerFiscalProfile.taxSystem')}
                          {...form.getInputProps('customerFiscalProfile.taxSystem')}
                          searchable
                          clearable={false}
                          label={t('FieldName:tax_system')}
                          data={Object.keys(RegimenFiscalEnum).map((code) => ({
                            value: code,
                            label: `${code} - ${RegimenFiscalEnum[code as RegimenFiscalType]}`
                          }))}
                        />
                        <TextInput
                          key={form.key('customerFiscalProfile.zipCode')}
                          {...form.getInputProps('customerFiscalProfile.zipCode')}
                          label={t('FieldName:zipCode')}
                          inputMode="numeric"
                          maxLength={5}
                        />
                        <Alert color="blue" icon={<IconInfoCircle />}>
                          {t('OrdersPage:createCFDI.customerFiscalDataSaveNotice')}
                        </Alert>
                      </>
                    ) : (
                      <>
                        <Text fw="bold">{t('FieldName:legal_name')}</Text>
                        <Text>{CFDIInfo?.customer?.legal_name || '-'}</Text>
                        <Text fw="bold">{t('FieldName:rfc')}</Text>
                        <Text>{CFDIInfo?.customer?.tax_id || '-'}</Text>
                        <Text fw="bold">{t('FieldName:email')}</Text>
                        <Text>{CFDIInfo?.customer?.email || '-'}</Text>
                        <Text fw="bold">{t('FieldName:tax_system')}</Text>
                        <Text>
                          {CFDIInfo?.customer?.tax_system
                            ? `${CFDIInfo.customer.tax_system} - ${
                                RegimenFiscalEnum[
                                  CFDIInfo.customer.tax_system as RegimenFiscalType
                                ] || ''
                              }`
                            : '-'}
                        </Text>
                        <Text fw="bold">{t('FieldName:zipCode')}</Text>
                        <Text>{CFDIInfo?.customer?.address?.zip || '-'}</Text>
                        {!canEditCustomerFiscalData && hasMissingCustomerFiscalData && (
                          <Alert color="yellow" icon={<IconInfoCircle />} mt="sm">
                            {t('OrdersPage:createCFDI.customerFiscalDataPermissionRequired')}
                          </Alert>
                        )}
                      </>
                    )}
                    </Flex>
                  </Grid.Col>
                )}
                {isCFDIGlobal ? (
                  <Grid.Col span={12} hidden={!showPUEPaymentFormAlert}>
                    <Alert color="yellow" icon={<IconInfoCircle />}>
                      {t('OrdersPage:createCFDI.globalPpdWarning')}
                    </Alert>
                  </Grid.Col>
                ) : (
                  <Grid.Col span={12} hidden={!showPUEPaymentFormAlert}>
                    <Alert color="yellow" icon={<IconInfoCircle />}>
                      {t('OrdersPage:createCFDI.ppdWarning')}
                    </Alert>
                  </Grid.Col>
                )}
                <Grid.Col span={{ xs: 12, md: 6 }}>
                  <DateInput
                    {...form.getInputProps('date')}
                    label={t('FieldName:date')}
                    placeholder={t('FieldName:date')}
                  />
                </Grid.Col>
                <Grid.Col span={{ xs: 12, md: 6 }}>
                  <Select
                    {...form.getInputProps('use')}
                    clearable={false}
                    label={t('FieldName:cfdi_use')}
                    defaultValue={CFDIInfo?.use}
                    data={useCFDIOptions}
                  />
                </Grid.Col>
                <Grid.Col span={{ xs: 12, md: 6 }}>
                  <Select
                    {...form.getInputProps('payment_method')}
                    defaultValue={CFDIInfo?.payment_method}
                    clearable={false}
                    label={t('FieldName:paymentMethod')}
                    // onChange={onMetodoPagoCFDIChange}
                    data={Object.keys(MetodoPagoCFDIEnum).map((key: string) => ({
                      value: key,
                      key: key,
                      label: `${key} - ${MetodoPagoCFDIEnum[key as MetodoPagoCFDType]}`
                    }))}
                  />
                </Grid.Col>
                <Grid.Col span={{ xs: 12, md: 6 }}>
                  <Select
                    {...form.getInputProps('payment_form')}
                    defaultValue={CFDIInfo?.payment_form}
                    clearable={false}
                    label={t('FieldName:paymentForm')}
                    disabled={disablePaymentFormField}
                    data={Object.keys(FormaPagoCFDIEnum)
                      .sort()
                      .map((key: string) => ({
                        value: key,
                        key: key,
                        label: `${key} - ${FormaPagoCFDIEnum[key as FormaPagoCFDIType]}`
                      }))}
                  />
                </Grid.Col>
                {isCFDIGlobal && (
                  <>
                    <Grid.Col span={{ xs: 12, md: 6 }}>
                      <Select
                        {...form.getInputProps('global.periodicity')}
                        defaultValue={CFDIInfo?.global?.periodicity}
                        label={t('FieldName:periodicity')}
                        clearable={false}
                        data={Object.keys(SATPeriodicityEnum).map((key: string) => ({
                          key: SATPeriodicityEnum[key as SATPeriodicityType],
                          value: SATPeriodicityEnum[key as SATPeriodicityType],
                          label: `${t(
                            'FieldName:periodicityValues.' +
                              SATPeriodicityEnum[key as SATPeriodicityType]
                          )}`
                        }))}
                      />
                    </Grid.Col>
                    <Grid.Col span={{ xs: 12, md: 6 }}>
                      <Select
                        {...form.getInputProps('global.months')}
                        defaultValue={CFDIInfo?.global?.months}
                        clearable={false}
                        label={t('FieldName:months')}
                        data={Object.keys(SATMonthsEnum).map((key: string) => ({
                          key: SATMonthsEnum[key as SATMonthsType],
                          value: SATMonthsEnum[key as SATMonthsType],
                          label: `${SATMonthsEnum[key as SATMonthsType]} - ${key}`
                        }))}
                      />
                    </Grid.Col>
                    <Grid.Col span={{ xs: 12, md: 6 }}>
                      <Select
                        {...form.getInputProps('global.year')}
                        defaultValue={CFDIInfo?.global?.year}
                        clearable={false}
                        label={t('FieldName:year')}
                        data={years}
                      />
                    </Grid.Col>
                  </>
                )}
                <Grid.Col span={{ xs: 12, md: 12 }}>
                  <Tooltip label={t('CFDIPage:globalPaymentMethod.tooltips.sendEmailInvoice')}>
                    <Switch
                      {...form.getInputProps('sendEmailInvoice', { type: 'checkbox' })}
                      label={t('FieldName:sendEmailInvoice')}
                    />
                  </Tooltip>
                </Grid.Col>
                <Grid.Col span={{ xs: 12, md: 12 }}>
                  <Table striped highlightOnHover withTableBorder withColumnBorders width={'100%'}>
                    <Table.Thead key="table-header">
                      <Table.Tr>
                        <Table.Th>{t('OrdersPage:createCFDI.tableHeaders.productKey')}</Table.Th>
                        <Table.Th>{t('OrdersPage:createCFDI.tableHeaders.description')}</Table.Th>
                        <Table.Th>{t('OrdersPage:createCFDI.tableHeaders.unitKey')}</Table.Th>
                        <Table.Th>{t('OrdersPage:createCFDI.tableHeaders.sku')}</Table.Th>
                        <Table.Th>{t('OrdersPage:createCFDI.tableHeaders.unitPrice')}</Table.Th>
                        <Table.Th>{t('OrdersPage:createCFDI.tableHeaders.quantity')}</Table.Th>
                        <Table.Th>{t('OrdersPage:createCFDI.tableHeaders.discount')}</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody key="table-body">{rows}</Table.Tbody>
                  </Table>
                </Grid.Col>
              </>
            )}
            <Grid.Col span={12}>
              <Button
                type="submit"
                fullWidth
                loading={isLoading}
                disabled={!hasCFDIPreview}
              >
                {t('CommonButtons:createInvoice')}
              </Button>
            </Grid.Col>
          </Grid>
        </form>
      )}
    </Modal>
  );
});
